import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Container, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import DynamicForm, { DynamicFormRef, setCurrencySymbolForMoneyInputs } from "@mavichpe/dynamic-forms";
import DefaultOffCanvas, { DefaultOffCanvasProps } from "../DefaultOffCanvas";
import styles from "./style.module.scss";
import { OpenShippingCostPubSub, OpenShippingCostPubSubPayload } from "@utils/pubsubs/PubSub";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { addProduct, setDocumentOtherCharges, setDocumentShippingGuide } from "@/store/slices/eDocumentSlides";
import { showAlert } from "@utils/Alert";
import {
    ClientShippingMode,
    ClientShippingOption,
    SHIPPING_CHARGE_DETALLE,
    describeShippingOption,
    hasValidThirdPartyData,
    shippingChargeFromProvider,
} from "@utils/ClientShipping";
import {
    MAX_OTHER_CHARGES,
    THIRD_PARTY_NAME_LENGTH_MESSAGE,
    isValidThirdPartyName,
} from "@utils/DocumentOtherCharges";
import { ShippingProviderApi, ShippingProviderDataType } from "@/api/ShippingProviderApi";
import { ProductApi } from "@/api/ProductsApi";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { CURRENCIES_SYMBOLS, COLON_CURRENCY_ID, TAXES_PERCENTAGES_VALUES } from "@utils/Constants";
import { removeFunctionFromObject } from "@utils/Utils";
import { formatMoneyAmount } from "@utils/EDocument";
import ShippingCostFormConfig, { MANUAL_SHIPPING_OPTION } from "./form";
import { validateIdentification } from "@utils/Identification";
import {
    resolveTaxpayer,
    TAXPAYER_NOT_REGISTERED_MESSAGE,
    TAXPAYER_UNVERIFIED_MESSAGE,
} from "@utils/taxpayerRegistry";

type ShippingCostFormData = {
    optionId: string,
    mode: string,
    providerId: string,
    amount: number,
    detalle: string,
    print_guide: boolean,
    bultos: number,
}

type ShippingCostOffCanvasProps = {} & DefaultOffCanvasProps;

/**
 * Agrega el costo de envio al documento. El usuario elige entre las opciones configuradas
 * para el cliente (un cliente puede tener varios couriers, o courier y envio propio), o
 * captura una a mano para este documento.
 *
 * Cual de los dos caminos se toma lo decide la modalidad de la opcion elegida, y la
 * diferencia es fiscal:
 * - `own`: el envio lo hace el suscriptor, asi que es un servicio propio y entra como
 *   linea del documento con el CABYS del producto configurado en el perfil.
 * - `external`: lo hace un courier y el suscriptor solo cobra por cuenta ajena, asi que
 *   entra como OtrosCargos tipo "04" con los datos fiscales del tercero.
 */
const ShippingCostOffCanvas = (_props: ShippingCostOffCanvasProps) => {
    const dispatch = useAppDispatch();
    const formRef = useRef<DynamicFormRef>(null);
    const shippingOptions = useAppSelector((state) => state.eDocument.documentShippingOptions);
    const documentOtherCharges = useAppSelector((state) => state.eDocument.documentOtherCharges);
    const shippingProductId = useAppSelector((state) => state.suscription.suscription?.shipping_product_id);
    const createShippingGuide = useAppSelector((state) => state.suscription.suscription?.create_shipping_guide);
    /** En envio propio no hay tercero, asi que el transporte de la guia es el negocio mismo. */
    const ownCarrierName = useAppSelector((state) => {
        const suscription = state.suscription.suscription;
        return `${suscription?.nombre_comercial || suscription?.nombre || ""}`;
    });
    const currencySymbol = useAppSelector((state) => state.currency.currencySymbol) || "";
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [providers, setProviders] = useState<ShippingProviderDataType[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    /**
     * Solo la factura persiste bultos y transporte, asi que la guia se ofrece unicamente
     * ahi: en una cotizacion el checkbox no tendria donde guardarse.
     */
    const [supportsGuide, setSupportsGuide] = useState<boolean>(false);
    /** Espejo sincrono del borrador: `onInputChange` corre antes de que el estado se aplique. */
    const draftRef = useRef<Partial<ShippingCostFormData>>({});

    const symbol = currencySymbol || CURRENCIES_SYMBOLS[COLON_CURRENCY_ID];
    const money = (amount: number): string => `${symbol} ${formatMoneyAmount(amount)}`;

    useEffect(() => {
        const unsubscribe = OpenShippingCostPubSub.subscribe(handleOpenEvent);
        return () => {
            unsubscribe();
        };
    }, [shippingOptions, providers, documentOtherCharges, shippingProductId, createShippingGuide]);

    const handleOpenEvent = async (payload: OpenShippingCostPubSubPayload) => {
        const { openOffCanvas = false, documentKind = "Invoice" } = payload;
        if (!openOffCanvas) {
            setIsOpen(false);
            return;
        }
        const guideSupported = documentKind === "Invoice";
        await loadProviders();
        setSupportsGuide(guideSupported);
        setIsOpen(true);
        // El formulario se monta en este mismo render, asi que sembrarlo hay que diferirlo.
        setTimeout(() => {
            const initial = buildInitialValues(guideSupported);
            draftRef.current = initial;
            formRef.current?.resetFormValue(initial);
        }, 0);
    };

    /**
     * No se cachea en el componente a proposito: `ShippingProvider` esta en
     * CACHEABLE_MODELS, asi que `fetchAll` ya evita la ida a la red, y su cache se
     * invalida al crear o editar. Guardarlo aqui ademas esconderia los proveedores dados
     * de alta sin recargar la pagina.
     */
    const loadProviders = async (): Promise<ShippingProviderDataType[]> => {
        const { items = [] } = await ShippingProviderApi.fetchAll();
        const activeProviders = items.filter((provider) => provider.active !== false);
        setProviders(activeProviders);
        return activeProviders;
    };

    /**
     * Con una sola opcion configurada se preselecciona: es el caso comun. La guia nace
     * marcada solo si el perfil la pidio, pero el usuario puede desmarcarla en el documento.
     */
    const buildInitialValues = (guideSupported: boolean): ShippingCostFormData => {
        const single = shippingOptions.length === 1 ? shippingOptions[0] : undefined;
        return {
            optionId: single?.id || "",
            mode: single?.mode || "",
            providerId: single?.providerId || "",
            amount: Number(single?.amount || 0),
            detalle: SHIPPING_CHARGE_DETALLE,
            print_guide: guideSupported && !!createShippingGuide,
            bultos: 1,
        };
    };

    /**
     * Al elegir una opcion guardada se copian su modalidad, proveedor y monto al formulario.
     * El monto queda editable: el costo pactado puede variar en un documento puntual.
     */
    const handleInputChange = useCallback((name: keyof ShippingCostFormData, value: unknown) => {
        draftRef.current = { ...draftRef.current, [name]: value as never };
        if (name !== "optionId") return;
        const optionId = `${value || ""}`;
        const option = shippingOptions.find((o) => o.id === optionId);
        if (!option) return;
        // Diferido: `onInputChange` corre ANTES de que dynamic-forms aplique el cambio.
        setTimeout(() => {
            formRef.current?.setFormValue("mode", option.mode, "String");
            formRef.current?.setFormValue("providerId", option.providerId || "", "String");
            formRef.current?.setFormValue("amount", option.amount || 0, "Number");
            draftRef.current = {
                ...draftRef.current,
                mode: option.mode,
                providerId: option.providerId || "",
                amount: option.amount || 0,
            };
        }, 0);
    }, [shippingOptions]);

    const isGuideField = (field: { name?: string }): boolean =>
        field?.name === "print_guide" || field?.name === "bultos";

    /** El simbolo tiene que llegar al input del monto, no solo a los textos del offcanvas. */
    const formConfig = useMemo(() => {
        const withCurrency = setCurrencySymbolForMoneyInputs(ShippingCostFormConfig, symbol);

        const optionEntries = shippingOptions.reduce((acc, option) => ({
            ...acc,
            [`${option.id}`]: `${describeShippingOption(option, providers)} — ${money(option.amount)}`,
        }), {} as Record<string, string>);
        // La captura manual siempre esta disponible: un envio puntual no obliga a tocar la
        // ficha del cliente.
        const optionOptions = { ...optionEntries, [MANUAL_SHIPPING_OPTION]: "Otro (capturar manualmente)" };

        const providerOptions = providers
            .filter((provider) => !!provider.id)
            .reduce((acc, provider) => ({
                ...acc,
                [`${provider.id}`]: `${provider.codigo || ""} - ${provider.nombre || ""}`,
            }), {} as Record<string, string>);

        return {
            ...withCurrency,
            sections: withCurrency.sections.map((section: any) => ({
                ...section,
                fields: section.fields
                    // En una cotizacion no hay donde guardar bultos ni transporte, asi que
                    // los campos de la guia ni se muestran.
                    .filter((row: { name?: string }[]) => supportsGuide || !row.some(isGuideField))
                    .map((row: any) =>
                        row.map((field: any) => {
                            if (field.name === "optionId") return { ...field, options: optionOptions };
                            if (field.name === "providerId") return { ...field, options: providerOptions };
                            return field;
                        })
                    ),
            })),
        };
    }, [symbol, providers, shippingOptions, supportsGuide]);

    const handleClose = () => {
        setIsOpen(false);
    };

    /**
     * Envio propio: se agrega como linea a partir del producto configurado en el perfil.
     * `forceRecalculatePrice` queda en false a proposito: el precio es el monto pactado
     * del envio, no el precio de lista del producto.
     */
    const addShippingLine = async (amount: number): Promise<boolean> => {
        if (!shippingProductId) {
            showAlert("danger", "No hay un producto de costo de envio configurado. Configurelo en Perfil de Usuario, campo \"Producto Facturacion de Costo de Envio\".");
            return false;
        }
        const product = await ProductApi.get(`${shippingProductId}`);
        if (!product?.id) {
            showAlert("danger", "El producto de costo de envio configurado en el perfil no existe o fue eliminado.");
            return false;
        }
        const productToAdd: EDocumentProductDataType = {
            productId: product.id,
            exento: product.exento,
            tipo_producto: product.tipo_producto,
            description: product.descripcion_fact,
            codigo: product.codigo,
            cant: 1,
            price: amount,
            porcentaje_impuesto: TAXES_PERCENTAGES_VALUES[`${product.porcentaje_impuesto}`],
            codigo_tarifa: product.porcentaje_impuesto,
            tipo_impuesto: product.tipo_impuesto,
            unidad_medida: product.unidad_medida,
            comision: 0,
            codigo_cabys: product.codigo_cabys,
            porcentaje_descuento: 0,
            descuento: 0,
            impuestos: 0,
            impuestos_servicio: 0,
            product: product,
            forceRecalculatePrice: false,
        };
        dispatch(addProduct(removeFunctionFromObject(productToAdd)));
        showAlert("success", "El costo de envio fue agregado como linea del documento");
        return true;
    };

    /**
     * Envio de un tercero: se agrega como OtrosCargos tipo "04".
     *
     * Aqui se verifica la identificacion del proveedor y no solo en su ficha, porque los
     * proveedores dados de alta antes de que existiera esa validacion siguen en la base con
     * cedulas invalidas o no inscritas, y Hacienda rechaza el comprobante con el codigo -105.
     */
    const addShippingCharge = async (providerId: string, amount: number, detalle: string): Promise<boolean> => {
        if (documentOtherCharges.length >= MAX_OTHER_CHARGES) {
            showAlert("danger", `El documento ya tiene el maximo de ${MAX_OTHER_CHARGES} cargos permitidos por Hacienda.`);
            return false;
        }
        const provider = providers.find((p) => p.id === providerId);
        if (!provider) {
            showAlert("danger", "Seleccione el proveedor que realiza el envio");
            return false;
        }
        // El nombre se revisa aparte del resto de los datos fiscales para poder decir que es
        // lo que hay que corregir: un nombre demasiado corto no es un dato faltante.
        if (!isValidThirdPartyName(provider.nombre)) {
            showAlert("danger", `El nombre del proveedor "${provider.nombre || ""}" no sirve para facturar. ${THIRD_PARTY_NAME_LENGTH_MESSAGE}`);
            return false;
        }
        if (!hasValidThirdPartyData(provider)) {
            showAlert("danger", `El proveedor "${provider.nombre || ""}" no tiene tipo y numero de identificacion. Hacienda rechaza un cobro de tercero sin esos datos.`);
            return false;
        }

        const format = validateIdentification(provider.tipo_identificacion, provider.identificacion);
        if (!format.valid) {
            showAlert("danger", `La identificacion del proveedor "${provider.nombre || ""}" no es valida: ${format.message || ""}`);
            return false;
        }

        const taxpayer = await resolveTaxpayer(provider.tipo_identificacion, provider.identificacion);
        if (taxpayer.status === "not_registered") {
            showAlert("danger", `El proveedor "${provider.nombre || ""}" no esta inscrito ante Tributacion. ${TAXPAYER_NOT_REGISTERED_MESSAGE}`);
            return false;
        }

        const charge = shippingChargeFromProvider(provider, amount, detalle);
        dispatch(setDocumentOtherCharges([...documentOtherCharges, charge]));
        showAlert("success", "El costo de envio fue agregado como otro cargo del documento");
        // Sin respuesta de Hacienda el cargo igual se agrega: un catalogo caido no puede
        // impedir facturar. El aviso queda para que el usuario lo revise antes de emitir.
        if (taxpayer.status === "unknown") {
            showAlert("warning", TAXPAYER_UNVERIFIED_MESSAGE);
        }
        return true;
    };

    /**
     * La opcion elegida manda sobre lo que quedo en el formulario: si el usuario escogio una
     * opcion guardada, su modalidad y proveedor salen de la ficha del cliente, no de los
     * campos (que en ese caso ni siquiera se muestran).
     */
    const resolveSelection = (formData: ShippingCostFormData): Pick<ClientShippingOption, "mode" | "providerId"> | null => {
        const optionId = `${formData.optionId || ""}`;
        if (optionId && optionId !== MANUAL_SHIPPING_OPTION) {
            const option = shippingOptions.find((o) => o.id === optionId);
            if (option) {
                return { mode: option.mode, providerId: option.providerId };
            }
        }
        const mode = `${formData.mode || ""}`;
        if (mode !== "own" && mode !== "external") {
            return null;
        }
        return { mode: mode as ClientShippingMode, providerId: `${formData.providerId || ""}` || undefined };
    };

    /**
     * Transporte que se imprime en la guia. Se resuelve aqui y no al imprimir porque queda
     * congelado en la factura: la reimpresion tiene que salir igual aunque despues se edite
     * o se borre el proveedor.
     */
    const resolveCarrierName = (selection: Pick<ClientShippingOption, "mode" | "providerId">): string => {
        if (selection.mode === "own") return ownCarrierName;
        const provider = providers.find((p) => p.id === selection.providerId);
        return `${provider?.nombre || ""}`;
    };

    const handleSubmit = async (formData: ShippingCostFormData) => {
        const selection = resolveSelection(formData);
        if (!selection) {
            showAlert("danger", "Seleccione la modalidad de envio");
            return;
        }
        const amount = Number(formData.amount || 0);
        if (amount <= 0) {
            showAlert("danger", "El monto del envio debe ser mayor a cero");
            return;
        }
        setIsSaving(true);
        try {
            const added = selection.mode === "own"
                ? await addShippingLine(amount)
                : await addShippingCharge(`${selection.providerId || ""}`, amount, `${formData.detalle || ""}`);
            if (added) {
                if (supportsGuide && formData.print_guide) {
                    dispatch(setDocumentShippingGuide({
                        bultos: Number(formData.bultos || 1),
                        carrier: resolveCarrierName(selection),
                    }));
                }
                setIsOpen(false);
            }
        } catch (error) {
            showAlert("danger", "No fue posible agregar el costo de envio al documento");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <DefaultOffCanvas
            className={styles.offCanvas}
            direction="end"
            isOpen={isOpen}
            onClosed={handleClose}
            toggle={() => setIsOpen(false)}
        >
            <OffcanvasHeader className={styles.offCanvasHeader} toggle={handleClose}>
                Agregar costo de envio
            </OffcanvasHeader>
            <OffcanvasBody className={styles.body}>
                <Container>
                    <div className={styles.summary} data-testid="shipping-cost-summary">
                        <div className={styles.summaryTitle}>Como se factura</div>
                        <div>
                            <strong>Envio propio:</strong> se agrega como linea del documento con el CABYS
                            del producto configurado en el perfil, y genera impuesto.
                        </div>
                        <div>
                            <strong>Envio por proveedor externo:</strong> se agrega como otro cargo tipo
                            04 (cobro de un tercero) con la identificacion del proveedor.
                        </div>
                        {shippingOptions.length === 0 && (
                            <div className={styles.warning} data-testid="shipping-cost-no-config">
                                El cliente no tiene opciones de envío configuradas. Elija "Otro (capturar
                                manualmente)" para agregar el envío solo en este documento.
                            </div>
                        )}
                    </div>
                    <DynamicForm<ShippingCostFormData>
                        ref={formRef}
                        innerForm={true}
                        formConfig={formConfig}
                        onInputChange={handleInputChange}
                        onSubmit={handleSubmit}
                    />
                </Container>
            </OffcanvasBody>
            <div className={styles.footer}>
                <Button color="secondary" type="button" onClick={handleClose}>
                    Cancelar
                </Button>
                <Button
                    color="primary"
                    type="button"
                    disabled={isSaving}
                    data-testid="shipping-cost-add"
                    onClick={() => formRef.current?.triggerFormSubmit()}
                >
                    Agregar al documento
                </Button>
            </div>
        </DefaultOffCanvas>
    );
};

export default ShippingCostOffCanvas;
